import type { IAmenity, ICoordinateGis, INoise, IPrivacy, ISite, ISubPhoto, IUser, IVehicle } from '@curbnturf/objects';

export namespace siteComplete {
  const testAmenity = (amenities?: IAmenity[]): { messages: string[]; completeness: number } => {
    // amenities always at least 5
    let completeness = 5;
    const messages: string[] = [];

    if (
      Array.isArray(amenities) &&
      amenities.find((amenity) => amenity.type === 'pullThrough' || amenity.type === 'backIn')
    ) {
      completeness += 4;
    } else {
      messages.push('Missing Pull Through or Back In Selection.');
    }

    return { messages, completeness };
  };

  const testDescriptionText = (description?: string): { messages: string[]; completeness: number } => {
    if (description && description.length >= 20) {
      return { messages: [], completeness: 4 };
    } else {
      return { messages: ['Missing Description Text'], completeness: 0 };
    }
  };

  const testDirections = (coordinates?: ICoordinateGis): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (coordinates) {
      if (coordinates.lat) {
        completeness += 2;
      } else {
        messages.push('Missing Latitude Coordinates');
      }

      if (coordinates.lon) {
        completeness += 2;
      } else {
        messages.push('Missing Longitude Coordinates');
      }
    }

    return { messages, completeness };
  };

  const testMaxGuests = (maxGuests?: number): { messages: string[]; completeness: number } => {
    if (maxGuests) {
      return { messages: [], completeness: 2 };
    } else {
      return { messages: ['Missing Max Guest Number'], completeness: 0 };
    }
  };

  const testName = (name?: string): { messages: string[]; completeness: number } => {
    if (name && name.length >= 8) {
      return { messages: [], completeness: 2 };
    } else {
      return { messages: ['Missing Site Name'], completeness: 0 };
    }
  };

  const testNoise = (noise?: INoise): { messages: string[]; completeness: number } => {
    let returnValue = 0;
    const messages: string[] = [];

    if (noise && noise.level) {
      returnValue += 4;
    } else {
      messages.push('Missing Noise Selection');
    }

    return { messages, completeness: returnValue };
  };

  const testSitePhotos = (photos?: ISubPhoto[]): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (photos && photos.length >= 3) {
      completeness += 6;
    } else {
      messages.push('Missing or Incomplete set of Photos');
    }

    return { messages, completeness };
  };

  const testPrice = (price?: number): { messages: string[]; completeness: number } => {
    if (price || price === 0) {
      return { messages: [], completeness: 8 };
    } else {
      return { messages: ['Missing Site Price'], completeness: 0 };
    }
  };

  const testPricing = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages: ['Missing Site for Pricing'], completeness };
    }

    const maxGuests = testMaxGuests(site.maxGuests);
    completeness += maxGuests.completeness;
    messages.push(...maxGuests.messages);

    const price = testPrice(site.price);
    completeness += price.completeness;
    messages.push(...price.messages);

    return { messages, completeness };
  };

  const testPrivacy = (privacy?: IPrivacy): { messages: string[]; completeness: number } => {
    const messages: string[] = [];
    if (privacy && (privacy.home || privacy.neighbor || privacy.others || privacy.secluded)) {
      return { messages, completeness: 4 };
    }

    messages.push('Missing Privacy Selection.');
    return { messages, completeness: 0 };
  };

  const testDescription = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages: ['Missing Site'], completeness };
    }

    const name = testName(site.name);
    completeness += name.completeness;
    messages.push(...name.messages);

    const descriptionTest = testDescriptionText(site.description);
    completeness += descriptionTest.completeness;
    messages.push(...descriptionTest.messages);

    const photos = testSitePhotos(site.photos);
    completeness += photos.completeness;
    messages.push(...photos.messages);

    return { messages, completeness };
  };

  const testUserName = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.firstName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile First Name');
    }

    if (user.lastName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile Last Name');
    }

    return { messages, completeness };
  };

  const testUserPhoto = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.photo?.key) {
      completeness += 4;
    } else {
      messages.push('Missing User Profile Photo');
    }

    return { messages, completeness };
  };

  const testUserPhone = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.phone) {
      completeness += 4;
    } else {
      messages.push('Missing Profile Phone Number');
    }

    return { messages, completeness };
  };

  export const userProfilePercentComplete = (user?: IUser): { messages: string[]; completeness: number } => {
    let completeness = 4;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['User Profile Missing'], completeness };
    }

    const userName = testUserName(user);
    completeness += userName.completeness;
    messages.push(...userName.messages);

    const userPhoto = testUserPhoto(user);
    completeness += userPhoto.completeness;
    messages.push(...userPhoto.messages);

    const userPhone = testUserPhone(user);
    completeness += userPhone.completeness;
    messages.push(...userPhone.messages);

    return { messages, completeness };
  };

  const testVehicles = (
    vehicles?: IVehicle,
    tentOnly?: boolean | null,
  ): { messages: string[]; completeness: number } => {
    const messages: string[] = [];
    if (
      vehicles &&
      (tentOnly ||
        vehicles.classA ||
        vehicles.classB ||
        vehicles.classC ||
        vehicles.fifthWheel ||
        vehicles.travelTrailer ||
        vehicles.teardrop ||
        vehicles.truckCamper ||
        vehicles.popup)
    ) {
      return { messages, completeness: 4 };
    }

    messages.push('Missing Vehicle Specifications');
    return { messages, completeness: 0 };
  };

  const testOverview = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages, completeness };
    }

    // on site activities
    completeness += 4;

    // off site activities
    completeness += 2;

    const amenities = testAmenity(site.amenities);
    completeness += amenities.completeness;
    messages.push(...amenities.messages);

    const vehicles = testVehicles(site.vehicles, site.tentOnly);
    completeness += vehicles.completeness;
    messages.push(...vehicles.messages);

    const privacy = testPrivacy(site.privacy);
    completeness += privacy.completeness;
    messages.push(...privacy.messages);

    const noise = testNoise(site.noise);
    completeness += noise.completeness;
    messages.push(...noise.messages);

    return { messages, completeness };
  };

  export const accommodationsCompleted = (amenities: IAmenity[]): boolean => {
    const completeness = testAmenity(amenities).completeness;

    return completeness >= 8;
  };

  export const descriptionTextCompleted = (site: ISite): boolean => {
    const completeness = testDescriptionText(site.description).completeness;

    return completeness >= 4;
  };

  export const directionsCompleted = (site: ISite): boolean => {
    const completeness = testDirections(site.coordinates).completeness;

    return completeness >= 4;
  };

  export const maxGuestsCompleted = (site: ISite): boolean => {
    const completeness = testMaxGuests(site.maxGuests).completeness;

    return completeness >= 2;
  };

  export const nameCompleted = (site: ISite): boolean => {
    const completeness = testName(site.name).completeness;

    return completeness >= 2;
  };

  export const noiseCompleted = (noise: INoise): boolean => {
    const completeness = testNoise(noise).completeness;

    return completeness >= 4;
  };

  export const overviewCompleted = (site: ISite): boolean => {
    const completeness = testOverview(site).completeness;

    return completeness >= 26; // 4 + 2 + 12 + 4
  };

  export const photosCompleted = (site: ISite): boolean => {
    const completeness = testSitePhotos(site.photos).completeness;

    return completeness >= 6;
  };

  export const priceCompleted = (site: ISite): boolean => {
    const completeness = testPrice(site?.price).completeness;

    return completeness >= 4;
  };

  export const privacyCompleted = (privacy: IPrivacy): boolean => {
    const completeness = testPrivacy(privacy).completeness;

    return completeness >= 4;
  };

  export const tentAmenity = (site: ISite): IAmenity | undefined => {
    if (!site.amenities) {
      return;
    }

    return site.amenities.find((amenity) => amenity.type === 'tents');
  };

  export const descriptionCompleted = (site: ISite): boolean => {
    return nameCompleted(site) && descriptionTextCompleted(site) && photosCompleted(site);
  };

  export const vehiclesCompleted = (vehicles?: IVehicle, tentOnly?: boolean | null): boolean => {
    const completeness = testVehicles(vehicles, tentOnly).completeness;

    return completeness >= 4;
  };

  export const userNameCompleted = (user: IUser): boolean => {
    return testUserName(user).completeness >= 4;
  };

  export const userPhotoCompleted = (user: IUser): boolean => {
    return testUserPhoto(user).completeness >= 4;
  };

  export const userPhoneCompleted = (user: IUser): boolean => {
    return testUserPhone(user).completeness >= 4;
  };

  export const userProfileCompleted = (user: IUser): boolean => {
    return userNameCompleted(user) && userPhotoCompleted(user) && userPhoneCompleted(user);
  };

  export const userCompleted = (user: IUser): boolean => {
    return userProfileCompleted(user) && userPhotoCompleted(user);
  };

  export const sitePercentComplete = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages: ['Missing Site'], completeness };
    }

    const overview = testOverview(site);
    completeness += overview.completeness;
    messages.push(...overview.messages);

    const description = testDescription(site);
    completeness += description.completeness;
    messages.push(...description.messages);

    const directions = testDirections(site.coordinates);
    completeness += directions.completeness;
    messages.push(...directions.messages);

    const pricing = testPricing(site);
    completeness += pricing.completeness;
    messages.push(...pricing.messages);

    return { messages, completeness: Math.floor((completeness / 103) * 100) };
  };

  export const completed = (site: ISite): boolean => {
    return sitePercentComplete(site).completeness >= 100;
  };

  export const siteCompleteMessages = (site: ISite): string[] => {
    const messages: string[] = [];

    if (!site) {
      return ['Missing Site'];
    }

    messages.push(...testOverview(site).messages);
    messages.push(...testDescription(site).messages);
    messages.push(...testDirections(site.coordinates).messages);

    messages.push(...testPricing(site).messages);

    return messages;
  };
}
